import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { LogIn, LogOut, User as UserIcon, ChevronDown } from "lucide-react";
import { useAuth } from "@/lib/auth";

export default function UserMenu() {
  const { user, loading, signIn, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const close = (e) => {
      if (!ref.current || ref.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  if (loading) {
    return (
      <span
        className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-white/10 text-white/40 text-xs"
        data-testid="user-menu-loading"
      >
        …
      </span>
    );
  }

  if (!user) {
    return (
      <button
        type="button"
        onClick={signIn}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 text-white/80 hover:text-[#EAB308] hover:border-[#EAB308] transition-colors duration-300 text-[0.7rem] uppercase tracking-[0.22em]"
        data-testid="signin-btn"
      >
        <LogIn className="w-3.5 h-3.5" /> Sign in
      </button>
    );
  }

  const initials = (user.name || user.email || "?")
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="relative" ref={ref} data-testid="user-menu">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-2 pl-1 pr-3 py-1 rounded-full border border-white/10 hover:border-[#EAB308] transition-colors duration-300"
        data-testid="user-menu-trigger"
      >
        {user.picture ? (
          <img
            src={user.picture}
            alt=""
            className="w-7 h-7 rounded-full object-cover"
            referrerPolicy="no-referrer"
          />
        ) : (
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#EAB308]/15 text-[#EAB308] text-[0.65rem] font-mono-rts">
            {initials}
          </span>
        )}
        <span className="hidden sm:inline text-[0.7rem] uppercase tracking-[0.22em] text-white/80 max-w-[9rem] truncate">
          {user.name}
        </span>
        <ChevronDown className="w-3.5 h-3.5 text-white/50" />
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-56 rts-glass rounded-xl overflow-hidden border border-white/10"
          data-testid="user-menu-dropdown"
        >
          <div className="px-4 py-3 border-b border-white/5">
            <p className="text-[0.62rem] font-mono-rts tracking-[0.3em] uppercase text-white/50">
              Signed in as
            </p>
            <p className="mt-1 font-serif-rts text-white truncate">
              {user.name}
            </p>
            <p className="text-xs text-white/40 truncate">{user.email}</p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/me")}
            className="w-full text-left px-4 py-3 text-sm text-white/80 hover:bg-white/5 hover:text-[#EAB308] transition-colors duration-200 flex items-center gap-2"
            data-testid="user-menu-profile"
          >
            <UserIcon className="w-3.5 h-3.5" /> Your journey
          </button>
          <button
            type="button"
            onClick={async () => {
              await signOut();
              navigate("/");
            }}
            className="w-full text-left px-4 py-3 text-sm text-white/80 hover:bg-white/5 hover:text-[#F87171] transition-colors duration-200 flex items-center gap-2"
            data-testid="user-menu-signout"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}
